import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  ExpandLess,
  ExpandMore,
  ReceiptLongOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import type { AxiosError } from "axios";
import apiService from "../../../service/Api";
import type { IStreamDetails } from "../../../types/streams";
import type {
  StreamFinancialReportData,
  StreamJournalEntry,
} from "./types";

interface StreamFinancialReportProps {
  streamId: number;
  stream: IStreamDetails;
  active: boolean;
}

const asNumber = (value: string | number | null | undefined) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value: string | number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(asNumber(value));

const metricTone = (value: string) =>
  asNumber(value) < 0 ? "error.main" : "text.primary";

const FinancialMetricCard = ({
  label,
  value,
  helper,
  color,
}: {
  label: string;
  value: string;
  helper: string;
  color?: string;
}) => (
  <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ my: 0.75, color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const StatementRow = ({
  label,
  value,
  secondary,
  total,
  rowIndex,
  amount,
  summary,
}: {
  label: string;
  value: string;
  secondary?: string;
  total?: boolean;
  rowIndex: number;
  amount?: number;
  summary?: boolean;
}) => {
  const isNegative = amount !== undefined && amount < 0;
  const backgroundColor = summary
    ? "#e2f0d9"
    : isNegative
      ? "#fce8e6"
      : rowIndex % 2 === 1
        ? "action.hover"
        : "transparent";
  const isBold = total || summary;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        px: 1,
        py: total ? 1.25 : 0.75,
        backgroundColor,
        borderTop: total ? "1px solid" : undefined,
        borderColor: "divider",
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={isBold ? 700 : 400}>
          {label}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary">
            {secondary}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" fontWeight={isBold ? 700 : 500}>
        {value}
      </Typography>
    </Box>
  );
};

const JournalEntryCard = ({ entry }: { entry: StreamJournalEntry }) => {
  const [open, setOpen] = useState(false);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Button
        fullWidth
        color="inherit"
        onClick={() => setOpen((current) => !current)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ p: 2, justifyContent: "space-between", textAlign: "left" }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={entry.origin} size="small" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              {entry.timestamp}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            {entry.description || "Journal entry"}
          </Typography>
        </Box>
      </Button>
      <Collapse in={open} unmountOnExit>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entry.transactions.map((transaction, index) => (
                <TableRow key={`${entry.uuid}-${transaction.account_code}-${index}`}>
                  <TableCell>
                    <Typography variant="body2">
                      {transaction.account_code} · {transaction.account_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transaction.account_role}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    {transaction.tx_type}
                  </TableCell>
                  <TableCell align="right">{money(transaction.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

const StreamFinancialReport = ({
  streamId,
  stream,
  active,
}: StreamFinancialReportProps) => {
  const [report, setReport] = useState<StreamFinancialReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!active || hasLoaded) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiService
      .get<StreamFinancialReportData>(
        `/api/financial-reports/streams/${streamId}/`,
      )
      .then((response) => {
        if (!cancelled) {
          setReport(response.data);
          setHasLoaded(true);
        }
      })
      .catch((requestError: AxiosError<{ detail?: string }>) => {
        if (!cancelled) {
          setError(
            requestError.response?.data?.detail ||
              "The financial report could not be loaded.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, hasLoaded, retryToken, streamId]);

  const reconciliationWarnings = useMemo(() => {
    if (!report) return [];

    const warnings: string[] = [];
    if (!report.revenue_journal_entry_uuid) {
      warnings.push("Recognized revenue has no supporting journal entry.");
    }
    if (
      report.latest_approved_cost_analysis &&
      !report.latest_approved_cost_analysis.journal_entry_uuid
    ) {
      warnings.push(
        "The latest approved Cost Analysis has no supporting accrual journal entry.",
      );
    }
    if (report.crew_gigs.some((gig) => !gig.journal_entry_uuid)) {
      warnings.push("One or more completed crew gigs are missing journal entries.");
    }
    if (asNumber(report.referral_expense) > 0 && !report.referral_details?.journal_entry_uuid) {
      warnings.push("The referral expense has no supporting journal entry.");
    }
    if (report.journal_entries_count === 0) {
      warnings.push("No accounting journal entries were found for this Stream.");
    }
    return warnings;
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setError(null);
              setRetryToken((current) => current + 1);
            }}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!report) return null;

  const breakdown = report.latest_approved_cost_analysis?.breakdown;

  return (
    <Stack spacing={3}>
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Stream Financial Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stream #{report.stream_id} · {report.stream_title}
              {report.booking_id ? ` · Booking #${report.booking_id}` : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={stream.status || "Status unavailable"} variant="outlined" />
            <Chip
              label={stream.end_stream ? "Completed" : "In progress"}
              color={stream.end_stream ? "success" : "default"}
            />
          </Stack>
        </Stack>
      </Box>

      {reconciliationWarnings.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberOutlined />}>
          <Typography variant="subtitle2" fontWeight={700}>
            Reconciliation warning
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {reconciliationWarnings.map((warning) => (
              <li key={warning}>
                <Typography variant="body2">{warning}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Alert severity="info">
        Cost Analysis amounts are approved accruals and do not confirm cash
        payment. Customer payment status is not included because a stream-specific
        payment allocation is unavailable.
      </Alert>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <FinancialMetricCard
            label="Recognized revenue"
            value={money(report.revenue)}
            helper="Revenue recognized for this stream"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <FinancialMetricCard
            label="Total expenses"
            value={money(report.total_expenses)}
            helper="Direct costs plus referral expense"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <FinancialMetricCard
            label="Contribution profit"
            value={money(report.stream_profit)}
            helper="After direct and referral expenses"
            color={metricTone(report.stream_profit)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <FinancialMetricCard
            label="Contribution margin"
            value={`${asNumber(report.profit_margin_percent).toFixed(2)}%`}
            helper="Profit as a percentage of revenue"
            color={metricTone(report.stream_profit)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TrendingUpOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Income statement
              </Typography>
            </Stack>
            <Typography variant="overline" color="text.secondary">
              Revenue
            </Typography>
            <StatementRow
              label="Live Stream Revenue"
              value={money(report.revenue)}
              secondary="Account 4010 when supported by the attached journal"
              rowIndex={0}
              amount={asNumber(report.revenue)}
            />

            <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              Cost of sales
            </Typography>
            <StatementRow
              label="Crew direct labor"
              value={`(${money(report.actual_crew_expenses)})`}
              rowIndex={1}
              amount={-asNumber(report.actual_crew_expenses)}
            />
            <StatementRow
              label="Travel & logistics"
              value={`(${money(breakdown?.travel)})`}
              secondary="Approved Cost Analysis accrual"
              rowIndex={2}
              amount={-asNumber(breakdown?.travel)}
            />
            <StatementRow
              label="Data & network"
              value={`(${money(breakdown?.data)})`}
              secondary="Approved Cost Analysis accrual"
              rowIndex={3}
              amount={-asNumber(breakdown?.data)}
            />
            <StatementRow
              label="Catering & refreshments"
              value={`(${money(breakdown?.catering)})`}
              secondary="Approved Cost Analysis accrual"
              rowIndex={4}
              amount={-asNumber(breakdown?.catering)}
            />
            <StatementRow
              label="Other direct costs"
              value={`(${money(breakdown?.other)})`}
              secondary="Approved Cost Analysis accrual"
              rowIndex={5}
              amount={-asNumber(breakdown?.other)}
            />
            <StatementRow
              label="Approved non-crew direct-cost accruals"
              value={`(${money(report.cost_analysis_direct_expenses)})`}
              total
              rowIndex={6}
              amount={-asNumber(report.cost_analysis_direct_expenses)}
            />

            <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              Operating expenses
            </Typography>
            <StatementRow
              label="Referral commission"
              value={`(${money(report.referral_expense)})`}
              secondary="Operating expense; shown separately from cost of sales"
              rowIndex={7}
              amount={-asNumber(report.referral_expense)}
            />
            <StatementRow
              label="Contribution profit"
              value={money(report.stream_profit)}
              total
              rowIndex={8}
              amount={asNumber(report.stream_profit)}
              summary
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <ReceiptLongOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Source summary
              </Typography>
            </Stack>
            <StatementRow
              label="Revenue journal"
              value={report.revenue_journal_entry_uuid ? "Linked" : "Missing"}
              rowIndex={0}
            />
            <StatementRow
              label="Approved Cost Analysis"
              value={report.latest_approved_cost_analysis?.case_number || "None"}
              rowIndex={1}
            />
            <StatementRow
              label="Completed crew gigs"
              value={String(report.crew_gigs.length)}
              rowIndex={2}
            />
            <StatementRow
              label="Referral partner"
              value={report.referral_details?.referral_partner || "None"}
              rowIndex={3}
            />
            <StatementRow
              label="Journal entries"
              value={String(report.journal_entries_count)}
              total
              rowIndex={4}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AccountBalanceOutlined color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Accounting journal entries
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          {report.journal_entries.map((entry) => (
            <JournalEntryCard key={entry.uuid} entry={entry} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default StreamFinancialReport;
